import { useEffect, useRef, useState } from 'react'
import { campaignCanPlay, campaignIndex, campaignInterval, campaignSlides, campaignSwipe } from './campaign-carousel'

export default function CampaignCarousel() {
  const [index, setIndex] = useState(0)
  const [playing, setPlaying] = useState(true)
  const [hovered, setHovered] = useState(false)
  const [visible, setVisible] = useState(false)
  const [pageVisible, setPageVisible] = useState(true)
  const [reducedMotion, setReducedMotion] = useState(false)
  const [interacting, setInteracting] = useState(false)
  const [loaded, setLoaded] = useState(() => campaignSlides.map(() => false))
  const root = useRef<HTMLDivElement>(null)
  const pointer = useRef<{ id: number; x: number; y: number } | null>(null)
  const playIntent = useRef(true)
  const running = campaignCanPlay({ playing, hovered, visible, pageVisible, reducedMotion, interacting, imagesReady: loaded.every(Boolean) })

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const syncMotion = () => {
      setReducedMotion(preference.matches)
      if (preference.matches) setPlaying(false)
    }
    const syncVisibility = () => setPageVisible(!document.hidden)
    syncMotion()
    syncVisibility()
    preference.addEventListener('change', syncMotion)
    document.addEventListener('visibilitychange', syncVisibility)
    const observer = new IntersectionObserver(([entry]) => setVisible(entry.isIntersecting), { threshold: .15 })
    if (root.current) observer.observe(root.current)
    return () => {
      preference.removeEventListener('change', syncMotion)
      document.removeEventListener('visibilitychange', syncVisibility)
      observer.disconnect()
    }
  }, [])

  useEffect(() => {
    if (!running) return
    const timer = window.setTimeout(() => setIndex(value => campaignIndex(value + 1)), campaignInterval)
    return () => window.clearTimeout(timer)
  }, [running, index])

  function select(next: number) {
    setPlaying(false)
    setIndex(campaignIndex(next))
  }

  return <div ref={root} className="campaign-banner campaign-carousel" role="region" aria-roledescription="carousel" aria-label="สไลด์แบนเนอร์ EMPRESS OF WELLNESS"
    onPointerEnter={event => { if (event.pointerType === 'mouse') setHovered(true) }}
    onPointerLeave={event => { if (event.pointerType === 'mouse') setHovered(false) }}
    onFocusCapture={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setPlaying(false) }}
    onKeyDown={event => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
      event.preventDefault()
      select(event.key === 'Home' ? 0 : event.key === 'End' ? campaignSlides.length - 1 : index + (event.key === 'ArrowRight' ? 1 : -1))
    }}>
    <div className="campaign-viewport" id="campaign-slides" tabIndex={0} role="group" aria-label="ภาพแบนเนอร์ ปัดซ้ายขวาหรือใช้ปุ่มลูกศรเพื่อเปลี่ยนภาพ"
      onPointerDown={event => {
        if (!event.isPrimary || event.button !== 0) return
        pointer.current = { id: event.pointerId, x: event.clientX, y: event.clientY }
        setInteracting(true)
        event.currentTarget.setPointerCapture(event.pointerId)
      }}
      onPointerUp={event => {
        const start = pointer.current
        if (!start || start.id !== event.pointerId) return
        pointer.current = null
        setInteracting(false)
        const direction = campaignSwipe(event.clientX - start.x, event.clientY - start.y)
        if (direction) select(index + direction)
      }}
      onPointerCancel={() => { pointer.current = null; setInteracting(false) }}
      onLostPointerCapture={() => { pointer.current = null; setInteracting(false) }}>
      <div className="campaign-track" style={{ transform: `translate3d(-${index * 100}%, 0, 0)` }}>
        {campaignSlides.map((slide, slideIndex) => <div key={slide.id} className={`campaign-slide campaign-slide--${slide.id}`}
          role="group" aria-roledescription="slide" aria-label={`ภาพที่ ${slideIndex + 1} จาก ${campaignSlides.length}`} aria-hidden={index !== slideIndex}>
          <img src={slide.src} srcSet={slide.srcSet} sizes={slide.srcSet ? '100vw' : undefined}
            width={slide.width} height={slide.height} alt={slide.alt} fetchPriority={slideIndex === 0 ? 'high' : 'low'}
            loading="eager" decoding="async" draggable="false"
            onLoad={() => setLoaded(previous => previous.map((ready, item) => item === slideIndex || ready))}
            onError={() => setPlaying(false)} />
          {slide.id === 'original' && <div className="banner-brand">
            <span>2026</span><strong>EMPRESS</strong><span>OF WELLNESS</span>
            <p>THE PREMIER WELLNESS COMPETITION<br />FOR WOMEN 35+</p>
          </div>}
        </div>)}
      </div>
    </div>
    <div className="campaign-carousel-controls">
      <button type="button" className="campaign-arrow" aria-label="แบนเนอร์ภาพก่อนหน้า" aria-controls="campaign-slides" onClick={() => select(index - 1)}><span aria-hidden="true">‹</span></button>
      <div className="campaign-dots" role="group" aria-label="เลือกภาพแบนเนอร์">
        {campaignSlides.map((slide, slideIndex) => <button type="button" key={slide.id} className="campaign-dot"
          aria-label={`แสดงภาพที่ ${slideIndex + 1}: ${slide.title}`} aria-current={index === slideIndex ? 'true' : undefined}
          aria-controls="campaign-slides" onClick={() => select(slideIndex)}><span aria-hidden="true" /></button>)}
      </div>
      <button type="button" className="campaign-arrow" aria-label="แบนเนอร์ภาพถัดไป" aria-controls="campaign-slides" onClick={() => select(index + 1)}><span aria-hidden="true">›</span></button>
      <span className="campaign-control-divider" aria-hidden="true" />
      <button type="button" className="campaign-play" onPointerDown={() => { playIntent.current = playing }}
        onClick={event => setPlaying(event.detail === 0 ? !playing : !playIntent.current)} disabled={reducedMotion}
        aria-label={reducedMotion ? 'ปิดการเลื่อนอัตโนมัติตามการตั้งค่าลดการเคลื่อนไหว' : playing ? 'หยุดเลื่อนแบนเนอร์อัตโนมัติ' : 'เล่นแบนเนอร์อัตโนมัติ'}>
        <svg aria-hidden="true" viewBox="0 0 24 24" fill="currentColor">{playing ? <><rect x="7" y="5" width="3" height="14" rx="1" /><rect x="14" y="5" width="3" height="14" rx="1" /></> : <path d="M8 5v14l11-7Z" />}</svg>
      </button>
    </div>
    <p className="sr-only" aria-live={running ? 'off' : 'polite'} aria-atomic="true">ภาพที่ {index + 1} จาก {campaignSlides.length}: {campaignSlides[index].title}</p>
  </div>
}
